/**
 * Undoable toast — shows a toast with an "Ångra" (Undo) button.
 * The action runs immediately; if the user clicks undo within the
 * toast duration, the undo callback fires to restore prior state.
 *
 * @example
 * import { toast } from '@/lib/toast'
 * toast.undoable(
 *   () => deleteAddress(id),
 *   () => restoreAddress(id, snapshot),
 *   'Adress borttagen'
 * )
 */

import { useToastStore } from './toastStore'

const UNDO_DURATION_MS = 6000

export function undoable(
  action: () => void | Promise<void>,
  undo: () => void | Promise<void>,
  label: string,
): void {
  // Run the action immediately
  void action()

  const store = useToastStore.getState()
  const id = `undo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

  // Track whether undo was invoked
  let undone = false

  // Expose undo handler via a map so the Toast UI can invoke it
  undoRegistry.set(id, async () => {
    if (undone) return
    undone = true
    store.remove(id)
    await undo()
  })

  store.push({
    type: 'info',
    title: label,
    description: '__UNDOABLE__',
    durationMs: UNDO_DURATION_MS,
  })

  // Patch the id to our custom one — the store generates sequential IDs,
  // but we need a stable reference. We swap right after push.
  const messages = useToastStore.getState().messages
  const last = messages[messages.length - 1]
  if (last && last.description === '__UNDOABLE__') {
    useToastStore.setState({
      messages: messages.map((m) =>
        m === last ? { ...m, id, description: undefined } : m,
      ),
    })
  }

  // Cleanup registry after duration
  window.setTimeout(() => {
    undoRegistry.delete(id)
  }, UNDO_DURATION_MS + 500)
}

/**
 * Registry mapping toast IDs to undo callbacks.
 * The Toast UI reads from this to render the undo button.
 */
export const undoRegistry = new Map<string, () => void | Promise<void>>()
